import { decimalPlaces, useLocaleContext } from "../contexts/LocaleContext";

import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LengthUnits } from "convert-units/definitions/length";
import { VolumeFlowRateUnits } from "convert-units/definitions/volumeFlowRate";
import { scaleThreshold } from "d3-scale";
import { observer } from "mobx-react-lite";
import { Icon } from "trunx";
import config from "../config";
import convert from "../convert";
import { useRootStore } from "../models/Root";
import { ISensorDisplayStream } from "../models/SensorDisplay";

interface StreamMarkerProps {
  measure: ISensorDisplayStream["measure"];
  value: number | undefined;
  unit: LengthUnits | VolumeFlowRateUnits;
}

export const StreamM = observer(() => {
  const {
    layers: { baseMap },
  } = useRootStore();

  let color: string;
  let opacity: number;
  if (config.markers.missing) {
    color = config.markers.missing;
    opacity = 1;
  } else {
    color = baseMap.startsWith("Imagery") ? "white" : "black";
    opacity = 0.8;
  }

  return (
    <Icon style={{ fontSize: "1.2em" }}>
      <FontAwesomeIcon
        icon={faExclamationTriangle}
        color={color}
        opacity={opacity}
      />
    </Icon>
  );
});

const StreamMarker = ({
  measure,
  value,
  unit: fromUnit,
}: StreamMarkerProps) => {
  const {
    units: {
      stream: { stage: toHeightUnit, discharge: toDischargeUnit },
    },
  } = useLocaleContext();

  const valueForColorUnit = measure === "stage" ? "ft" : "ft3/s";
  try {
    const valueForColor = convert(value).from(fromUnit).to(valueForColorUnit);

    const toUnit = measure === "stage" ? toHeightUnit : toDischargeUnit;
    const value_ = convert(value)
      .from(fromUnit)
      .to(toUnit)
      .toFixed(decimalPlaces(toUnit));

    const {
      stageBreakValues: heightBreakValues,
      dischargeBreakValues,
      textColors,
      backgroundColors,
    } = config.markers.stream;
    const breakValues =
      measure === "stage" ? heightBreakValues : dischargeBreakValues;
    const streamTextColor = scaleThreshold(breakValues, textColors);
    const streamFill = scaleThreshold(breakValues, backgroundColors);

    const backgroundColor = streamFill(valueForColor);

    const color = streamTextColor(valueForColor);
    return (
      <span className="tag sensor-tag" style={{ color, backgroundColor }}>
        {value_}
      </span>
    );
  } catch (e) {
    return <StreamM />;
  }
};

export default StreamMarker;
